/**
 * Fredhopper (Attraqt XO) tracking SDK client.
 *
 * The XO SDK stub is loaded by AEM in the <head> tag (see the vendor's CDN snippet).
 * The stub defines `window.xo` synchronously and queues calls internally until the
 * real script has finished loading, so — unlike Bloomreach's brweb — there is no
 * "not started yet" race to manage here.
 *
 * Flow:
 *   1. AEM loads the SDK stub. window.xo exists and queues calls.
 *   2. React calls initFredhopper() after marketing consent (xo.activity.init()).
 *   3. Components/subscribers call trackFredhopper() to send activity events.
 *
 * If window.xo isn't present at all (e.g. local dev without AEM), events are
 * queued here and flushed once it becomes available.
 *
 * @see https://fredhopper.gitbook.io/product-discovery/tracking-and-sending-events/implementation-guide/sdk
 */
import { Cookie } from '@as-react/common-types';
import { CookieUtil } from '@as-react/common-utils';
import { v4 as uuidv4 } from 'uuid';
import { FREDHOPPER_SESSION_ID_STORAGE_KEY } from './constants';
/** Max buffered events before the SDK is available. Prevents unbounded memory growth. */
const MAX_QUEUE_SIZE = 100;
/** Events tracked before the SDK is ready. Flushed when window.xo becomes available. */
const eventQueue = [];
const state = {
    initialized: false,
    enabled: false,
    debug: false,
    sdkInitialized: false,
    trackerKey: '',
    region: undefined,
};
const log = (...args) => {
    if (state.debug) {
        console.debug('[Fredhopper]', ...args);
    }
};
/**
 * Resolves window.xo and, on first successful resolution, calls xo.activity.init().
 * Handles the race where React initializes before AEM's XO stub script has run.
 */
const ensureInitialized = () => {
    var _a;
    if (typeof window === 'undefined') {
        return undefined;
    }
    const sdk = ((_a = window.xo) === null || _a === void 0 ? void 0 : _a.activity) ? window.xo : undefined;
    if (sdk && !state.sdkInitialized) {
        sdk.activity.init({ trackerKey: state.trackerKey, region: state.region });
        state.sdkInitialized = true;
        log('Initialized');
    }
    return sdk;
};
/**
 * Resolves the visitor session id required by every Fredhopper activity event.
 * Reuses the site-wide Adobe visitor UUID cookie when present (consistent with
 * how other vendors, e.g. Criteo, reuse it) and otherwise falls back to a
 * generated id persisted in localStorage so the same session id survives reloads.
 */
const getSessionId = () => {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const adobeVisitorUuid = CookieUtil.get(Cookie._UUID);
    if (adobeVisitorUuid) {
        return adobeVisitorUuid;
    }
    try {
        const storedSessionId = window.localStorage.getItem(FREDHOPPER_SESSION_ID_STORAGE_KEY);
        if (storedSessionId) {
            return storedSessionId;
        }
        const generatedSessionId = uuidv4();
        window.localStorage.setItem(FREDHOPPER_SESSION_ID_STORAGE_KEY, generatedSessionId);
        return generatedSessionId;
    }
    catch (_a) {
        return undefined;
    }
};
const dispatchToSdk = (sdk, { action, target, metadata, sourceId }) => {
    const sessionId = getSessionId();
    if (!sessionId) {
        log('Skipped, no session id available', action);
        return;
    }
    log(action, target, metadata, sourceId);
    sdk.activity.send(Object.assign(Object.assign(Object.assign({ action,
        target }, (metadata && { metadata })), (sourceId && { sourceId })), { user: { identities: { sessionId } } }));
};
const flushQueue = (sdk) => {
    if (eventQueue.length === 0) {
        return;
    }
    log('Flushing', eventQueue.length, 'queued events');
    while (eventQueue.length > 0) {
        dispatchToSdk(sdk, eventQueue.shift());
    }
};
const processEvent = (event) => {
    const sdk = ensureInitialized();
    if (!sdk) {
        if (eventQueue.length < MAX_QUEUE_SIZE) {
            eventQueue.push(event);
        }
        return;
    }
    flushQueue(sdk);
    dispatchToSdk(sdk, event);
};
export const initFredhopper = (config, debug = false) => {
    if (state.initialized) {
        return;
    }
    state.initialized = true;
    if (!config.isEnabled) {
        return;
    }
    state.enabled = true;
    state.debug = debug;
    state.trackerKey = config.trackerKey;
    state.region = config.region;
    const sdk = ensureInitialized();
    if (sdk) {
        flushQueue(sdk);
    }
};
export const trackFredhopper = (action, target, metadata, sourceId) => {
    if (!state.enabled) {
        if (eventQueue.length < MAX_QUEUE_SIZE) {
            eventQueue.push({ action, target, metadata, sourceId });
        }
        return;
    }
    // Tracking must never break the caller or block other subscribers on the
    // shared emitter — mitt aborts remaining listeners if one throws (see
    // node_modules/mitt: emit() uses Array.map, which doesn't catch).
    try {
        processEvent({ action, target, metadata, sourceId });
    }
    catch (e) {
        console.error('[Fredhopper] tracking error', e);
    }
};
/** Reset all state. Only for testing — do not use in production code. */
export const resetFredhopper = () => {
    state.initialized = false;
    state.enabled = false;
    state.debug = false;
    state.sdkInitialized = false;
    state.trackerKey = '';
    state.region = undefined;
    eventQueue.length = 0;
};
